import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, ChevronDown, ChevronUp, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
  adminGetQuizQuestions, adminCreateQuizQuestion, adminUpdateQuizQuestion, 
  adminDeleteQuizQuestion, adminCreateQuizAnswer, adminUpdateQuizAnswer, adminDeleteQuizAnswer
} from '@/lib/api';
import { toast } from 'sonner';

const AdminQuizQuestions = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [answerDialogOpen, setAnswerDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingAnswer, setEditingAnswer] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [formData, setFormData] = useState({
    question_text: '',
    question_type: 'single_select',
    sort_order: 0,
    active: true
  });
  const [answerFormData, setAnswerFormData] = useState({
    answer_value: '',
    answer_label: '',
    sort_order: 0
  });

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      const data = await adminGetQuizQuestions();
      setQuestions(data || []);
    } catch (error) {
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (questionId) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const openCreateDialog = () => {
    setEditingQuestion(null);
    setFormData({
      question_text: '',
      question_type: 'single_select',
      sort_order: questions.length,
      active: true
    });
    setDialogOpen(true);
  };

  const openEditDialog = (question) => {
    setEditingQuestion(question);
    setFormData({
      question_text: question.question_text,
      question_type: question.question_type,
      sort_order: question.sort_order,
      active: question.active !== false
    });
    setDialogOpen(true);
  };

  const openAnswerDialog = (question, answer = null) => {
    setSelectedQuestion(question);
    setEditingAnswer(answer);
    if (answer) {
      setAnswerFormData({
        answer_value: answer.answer_value,
        answer_label: answer.answer_label,
        sort_order: answer.sort_order
      });
    } else {
      setAnswerFormData({
        answer_value: '',
        answer_label: '',
        sort_order: question.answers?.length || 0
      });
    }
    setAnswerDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingQuestion) {
        await adminUpdateQuizQuestion(editingQuestion.id, formData);
        toast.success('Question updated');
      } else {
        await adminCreateQuizQuestion(formData);
        toast.success('Question created');
      }
      setDialogOpen(false);
      loadQuestions();
    } catch (error) {
      toast.error('Failed to save question');
    }
  };

  const handleAnswerSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...answerFormData,
        question_id: selectedQuestion.id
      };
      if (editingAnswer) {
        await adminUpdateQuizAnswer(editingAnswer.id, data);
        toast.success('Answer updated');
      } else {
        await adminCreateQuizAnswer(data);
        toast.success('Answer created');
      }
      setAnswerDialogOpen(false);
      loadQuestions();
    } catch (error) {
      toast.error('Failed to save answer');
    }
  };

  const handleDeleteQuestion = async (question) => {
    if (!window.confirm('Delete this question and all its answers?')) return;
    try {
      await adminDeleteQuizQuestion(question.id);
      toast.success('Question deleted');
      loadQuestions();
    } catch (error) {
      toast.error('Failed to delete question');
    }
  };

  const handleDeleteAnswer = async (answer) => {
    if (!window.confirm('Delete this answer?')) return;
    try {
      await adminDeleteQuizAnswer(answer.id);
      toast.success('Answer deleted');
      loadQuestions();
    } catch (error) {
      toast.error('Failed to delete answer');
    }
  };

  if (loading) {
    return <div className="text-stone-400 p-8">Loading questions...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl text-[#2C2C2C] mb-1">Quiz Questions</h1>
          <p className="text-stone-600 text-sm">{questions.length} questions in quiz</p>
        </div>
        <Button onClick={openCreateDialog} className="btn-primary" data-testid="add-question-btn">
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Add Question
        </Button>
      </div>

      {questions.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <p className="text-stone-500 mb-4">No quiz questions created yet</p>
            <Button onClick={openCreateDialog} variant="outline" className="btn-outline">
              Add Your First Question
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {questions.sort((a, b) => a.sort_order - b.sort_order).map((question, index) => (
            <Card key={question.id} data-testid={`question-card-${question.id}`}>
              <Collapsible
                open={expandedQuestions[question.id]}
                onOpenChange={() => toggleExpanded(question.id)}
              >
                <CardHeader className="py-4">
                  <div className="flex items-start gap-4">
                    <div className="flex items-center gap-2 text-stone-400">
                      <GripVertical className="w-4 h-4" strokeWidth={1.5} />
                      <span className="text-sm font-medium">{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-base font-medium">{question.question_text}</CardTitle>
                          <div className="flex gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">
                              {question.question_type === 'multi_select' ? 'Multi-select' : 'Single select'}
                            </Badge>
                            <Badge className={question.active ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-600'}>
                              {question.active ? 'Active' : 'Inactive'}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {question.answers?.length || 0} answers
                            </Badge>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); openEditDialog(question); }}
                            className="h-8 w-8 text-stone-400 hover:text-[#4A6763]"
                          >
                            <Pencil className="w-4 h-4" strokeWidth={1.5} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); handleDeleteQuestion(question); }}
                            className="h-8 w-8 text-stone-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </Button>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-stone-400">
                              {expandedQuestions[question.id] ? (
                                <ChevronUp className="w-4 h-4" strokeWidth={1.5} />
                              ) : (
                                <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
                              )}
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CollapsibleContent>
                  <CardContent className="pt-0 pb-4">
                    <div className="ml-10 space-y-2">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs uppercase tracking-wide text-stone-500 font-medium">Answers</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openAnswerDialog(question)}
                          className="h-7 text-xs"
                          data-testid={`add-answer-${question.id}`}
                        >
                          <Plus className="w-3 h-3 mr-1" strokeWidth={1.5} />
                          Add Answer
                        </Button>
                      </div>
                      {question.answers?.length === 0 ? (
                        <p className="text-sm text-stone-400 py-2">No answers yet</p>
                      ) : (
                        question.answers?.sort((a, b) => a.sort_order - b.sort_order).map((answer) => (
                          <div 
                            key={answer.id}
                            className="flex items-center justify-between p-2 bg-stone-50 rounded-lg"
                          >
                            <div>
                              <p className="text-sm text-[#2C2C2C]">{answer.answer_label}</p>
                              <code className="text-xs text-stone-500">{answer.answer_value}</code>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openAnswerDialog(question, answer)}
                                className="h-7 w-7 text-stone-400 hover:text-[#4A6763]"
                              >
                                <Pencil className="w-3 h-3" strokeWidth={1.5} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteAnswer(answer)}
                                className="h-7 w-7 text-stone-400 hover:text-red-500"
                              >
                                <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          ))}
        </div>
      )}

      {/* Question Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editingQuestion ? 'Edit Question' : 'Add Question'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <label className="form-label">Question Text</label>
                <Textarea
                  value={formData.question_text}
                  onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                  placeholder="Enter your question..."
                  rows={3}
                  required
                  data-testid="question-text-input"
                />
              </div>
              <div>
                <label className="form-label">Question Type</label>
                <Select
                  value={formData.question_type}
                  onValueChange={(value) => setFormData({ ...formData, question_type: value })}
                >
                  <SelectTrigger data-testid="question-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single_select">Single Select</SelectItem>
                    <SelectItem value="multi_select">Multi Select</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="form-label">Sort Order</label>
                <Input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  data-testid="question-order-input"
                />
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                  data-testid="question-active-input"
                />
                <label className="text-sm text-stone-600">Active (shown in quiz)</label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="save-question-btn">
                {editingQuestion ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Answer Dialog */}
      <Dialog open={answerDialogOpen} onOpenChange={setAnswerDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editingAnswer ? 'Edit Answer' : 'Add Answer'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAnswerSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <label className="form-label">Answer Label (Displayed)</label>
                <Input
                  value={answerFormData.answer_label}
                  onChange={(e) => setAnswerFormData({ ...answerFormData, answer_label: e.target.value })}
                  placeholder="e.g., Digestive health"
                  required
                  data-testid="answer-label-input"
                />
              </div>
              <div>
                <label className="form-label">Answer Value (Internal)</label>
                <Input
                  value={answerFormData.answer_value}
                  onChange={(e) => setAnswerFormData({ ...answerFormData, answer_value: e.target.value })}
                  placeholder="e.g., digestive"
                  required
                  data-testid="answer-value-input"
                />
              </div>
              <div>
                <label className="form-label">Sort Order</label>
                <Input
                  type="number"
                  value={answerFormData.sort_order}
                  onChange={(e) => setAnswerFormData({ ...answerFormData, sort_order: parseInt(e.target.value) || 0 })}
                  data-testid="answer-order-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAnswerDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="save-answer-btn">
                {editingAnswer ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminQuizQuestions;
